import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceTable = ({
  services = [],
  onEdit,
  onDelete,
  onBulkAction,
  selectedServices = [],
  onServiceSelect,
  onSelectAll,
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fa-IR')?.format(price) + ' تومان';
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours} ساعت و ${mins} دقیقه`;
    } else if (hours > 0) {
      return `${hours} ساعت`;
    } else {
      return `${mins} دقیقه`;
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedServices = [...services]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    if (typeof aValue === 'string') {
      aValue = aValue?.toLowerCase();
      bValue = bValue?.toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const isAllSelected =
    services?.length > 0 && selectedServices?.length === services?.length;
  const isPartiallySelected =
    selectedServices?.length > 0 && selectedServices?.length < services?.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(services?.map((service) => service?.id));
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ArrowUpDown';
    return sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStatusBadge = (assignedEmployees) => {
    const count = assignedEmployees?.length || 0;
    if (count === 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
          تخصیص نشده
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
        {count} کارمند
      </span>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3 text-center">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isPartiallySelected;
                  }}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                  نام خدمت
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('duration')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                  مدت زمان
                  <Icon name={getSortIcon('duration')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <button
                  onClick={() => handleSort('price')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                  قیمت
                  <Icon name={getSortIcon('price')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-sm font-medium text-foreground">
                  وضعیت تخصیص
                </span>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-medium text-foreground">
                  عملیات
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedServices?.map((service) => (
              <tr
                key={service?.id}
                className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedServices?.includes(service?.id)}
                    onChange={(e) =>
                      onServiceSelect(service?.id, e?.target?.checked)
                    }
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {service?.name}
                    </p>
                    {service?.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {service?.description}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">
                    {formatDuration(service?.duration)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">
                    {formatPrice(service?.price)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(service?.assignedEmployees)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(service)}
                      iconName="Edit"
                      iconSize={16}
                      className="text-muted-foreground hover:text-primary">
                      <span className="sr-only">ویرایش خدمت</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(service)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-muted-foreground hover:text-error">
                      <span className="sr-only">حذف خدمت</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {sortedServices?.map((service) => (
          <div key={service?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={selectedServices?.includes(service?.id)}
                  onChange={(e) =>
                    onServiceSelect(service?.id, e?.target?.checked)
                  }
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 mt-1"
                />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-foreground">
                    {service?.name}
                  </h3>
                  {service?.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {service?.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(service)}
                  iconName="Edit"
                  iconSize={16}
                  className="text-muted-foreground hover:text-primary">
                  <span className="sr-only">ویرایش</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(service)}
                  iconName="Trash2"
                  iconSize={16}
                  className="text-muted-foreground hover:text-error">
                  <span className="sr-only">حذف</span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">مدت زمان:</span>
                <p className="text-foreground font-medium">
                  {formatDuration(service?.duration)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">قیمت:</span>
                <p className="text-foreground font-medium">
                  {formatPrice(service?.price)}
                </p>
              </div>
            </div>

            <div className="mt-3">
              <span className="text-muted-foreground text-sm">
                وضعیت تخصیص:
              </span>
              <div className="mt-1">
                {getStatusBadge(service?.assignedEmployees)}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty State */}
      {services?.length === 0 && (
        <div className="p-8 text-center">
          <Icon
            name="Scissors"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            هیچ خدمتی یافت نشد
          </h3>
          <p className="text-muted-foreground">
            برای شروع، اولین خدمت خود را اضافه کنید
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceTable;
